import { Mail, Send, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import configService, { DropdownOption } from '../services/configService';
import demoRequestService, { DemoRequestData } from '../services/demoRequestService';
import { getCountryCodes } from '../utils/phoneValidation';
import { Dropdown, Input, PhoneInput, TextArea } from './forms';

interface RequestDemoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DemoRequestForm {
  fullName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  productType: string;
  message: string;
  captchaToken: string;
}

const RequestDemoDrawer: React.FC<RequestDemoDrawerProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { success, error: toastError } = useToast();
  const [formData, setFormData] = useState<DemoRequestForm>({
    fullName: '',
    email: '',
    phoneNumber: '',
    countryCode: '+1',
    productType: '',
    message: '',
    captchaToken: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaChallenge, setCaptchaChallenge] = useState({
    question: '',
    answer: '',
    userAnswer: '',
  });
  const [productTypes, setProductTypes] = useState<DropdownOption[]>([]);
  const [isLoadingProductTypes, setIsLoadingProductTypes] = useState(true);

  // Get country codes for phone input
  const countryCodes = getCountryCodes();

  // Fetch product types from backend
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        setIsLoadingProductTypes(true);
        const types = await configService.getDemoRequestProductTypes();
        setProductTypes(types);
      } catch {
        // Fallback to hardcoded options if API fails
        setProductTypes([
          {
            value: 'project-management',
            label: t('demoRequest.productTypes.projectManagement'),
            isActive: true,
            order: 1,
          },
          {
            value: 'project-financials',
            label: t('demoRequest.productTypes.projectFinancials'),
            isActive: true,
            order: 2,
          },
          {
            value: 'quality-safety',
            label: t('demoRequest.productTypes.qualitySafety'),
            isActive: true,
            order: 3,
          },
          {
            value: 'design-coordination',
            label: t('demoRequest.productTypes.designCoordination'),
            isActive: true,
            order: 4,
          },
          {
            value: 'invoice-management',
            label: t('demoRequest.productTypes.invoiceManagement'),
            isActive: true,
            order: 5,
          },
          {
            value: 'tender-management',
            label: t('demoRequest.productTypes.tenderManagement'),
            isActive: true,
            order: 6,
          },
          {
            value: 'full-platform',
            label: t('demoRequest.productTypes.fullPlatform'),
            isActive: true,
            order: 7,
          },
        ]);
      } finally {
        setIsLoadingProductTypes(false);
      }
    };

    if (isOpen) {
      fetchProductTypes();
    }
  }, [isOpen, t]);

  // Generate simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operations = ['+', '-', '*'];
    const operation = operations[Math.floor(Math.random() * operations.length)];

    let answer;
    let question;

    switch (operation) {
      case '+':
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case '-':
        answer = num1 - num2;
        question = `${num1} - ${num2}`;
        break;
      case '*':
        answer = num1 * num2;
        question = `${num1} × ${num2}`;
        break;
      default:
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
    }

    setCaptchaChallenge({
      question,
      answer: answer.toString(),
      userAnswer: '',
    });
  };

  // Initialize captcha when drawer opens
  React.useEffect(() => {
    if (isOpen) {
      generateCaptcha();
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof DemoRequestForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (phoneNumber: string) => {
    setFormData(prev => ({ ...prev, phoneNumber }));
    if (errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: '' }));
    }
  };

  const handleCountryCodeChange = (countryCode: string) => {
    setFormData(prev => ({ ...prev, countryCode }));
  };

  const handleCaptchaChange = (value: string) => {
    setCaptchaChallenge(prev => ({ ...prev, userAnswer: value }));
    if (errors.captcha) {
      setErrors(prev => ({ ...prev, captcha: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('demoRequest.validation.nameRequired');
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = t('demoRequest.validation.nameMinLength');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('demoRequest.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('demoRequest.validation.emailInvalid');
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('demoRequest.validation.phoneRequired');
    }

    if (!formData.productType) {
      newErrors.productType = t('demoRequest.validation.productTypeRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('demoRequest.validation.messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('demoRequest.validation.messageMinLength');
    }

    if (!captchaChallenge.userAnswer.trim()) {
      newErrors.captcha = t('demoRequest.validation.captchaRequired');
    } else if (captchaChallenge.userAnswer !== captchaChallenge.answer) {
      newErrors.captcha = t('demoRequest.validation.captchaIncorrect');
      generateCaptcha();
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: DemoRequestData = {
        ...formData,
        captchaToken: captchaChallenge.answer, // For now, using the answer as token
      };

      await demoRequestService.submitDemoRequest(requestData);

      // Success - show success message and close drawer
      success(
        t('demoRequest.toast.submissionSuccess'),
        t('demoRequest.toast.submissionSuccessMessage')
      );
      onClose();

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        countryCode: '+1',
        productType: '',
        message: '',
        captchaToken: '',
      });

      // Reset captcha
      generateCaptcha();
    } catch (error: any) {
      toastError(
        t('demoRequest.toast.submissionFailed'),
        error.message || t('demoRequest.toast.submissionFailedMessage')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[720px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header - Sticky */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center space-x-3">
              <div className="bg-brand-primary bg-opacity-10 p-2 rounded-lg">
                <Mail className="h-5 w-5 text-brand-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('demoRequest.title')}</h2>
                <p className="text-sm text-gray-600">{t('demoRequest.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <Input
                  label={t('demoRequest.form.fullName')}
                  type="text"
                  value={formData.fullName}
                  onChange={e => handleInputChange('fullName', e.target.value)}
                  error={errors.fullName}
                  placeholder={t('demoRequest.form.fullNamePlaceholder')}
                  required
                />

                {/* Email */}
                <Input
                  label={t('demoRequest.form.email')}
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  error={errors.email}
                  placeholder={t('demoRequest.form.emailPlaceholder')}
                  required
                />

                {/* Phone Number */}
                <PhoneInput
                  label={t('demoRequest.form.phoneNumber')}
                  value={formData.phoneNumber}
                  countryCodes={countryCodes}
                  selectedCountryCode={formData.countryCode}
                  onCountryCodeChange={handleCountryCodeChange}
                  onChange={handlePhoneChange}
                  error={errors.phoneNumber}
                  placeholder={t('demoRequest.form.phoneNumberPlaceholder')}
                  required
                />

                {/* Product Type */}
                <Dropdown
                  label={t('demoRequest.form.productType')}
                  options={productTypes}
                  value={formData.productType}
                  onChange={value => handleInputChange('productType', value)}
                  error={errors.productType}
                  placeholder={
                    isLoadingProductTypes
                      ? t('common.loading')
                      : t('demoRequest.form.productTypePlaceholder')
                  }
                  disabled={isLoadingProductTypes}
                  required
                />

                {/* Message */}
                <TextArea
                  label={t('demoRequest.form.message')}
                  value={formData.message}
                  onChange={value => handleInputChange('message', value)}
                  error={errors.message}
                  placeholder={t('demoRequest.form.messagePlaceholder')}
                  rows={4}
                  required
                />

                {/* Captcha */}
                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#1F1F1F] leading-[1.75em]">
                    {t('demoRequest.form.securityCheck')} <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-4">
                      <div className="text-lg font-mono bg-white px-3 py-2 rounded border">
                        {captchaChallenge.question} = ?
                      </div>
                      <Input
                        type="text"
                        value={captchaChallenge.userAnswer}
                        onChange={e => handleCaptchaChange(e.target.value)}
                        placeholder={t('demoRequest.form.answer')}
                        className="w-24"
                      />
                      <button
                        type="button"
                        onClick={generateCaptcha}
                        className="text-sm text-brand-primary hover:text-brand-primary-hover font-medium"
                      >
                        {t('demoRequest.form.newQuestion')}
                      </button>
                    </div>
                    {errors.captcha && (
                      <p className="mt-2 text-sm text-red-600">{errors.captcha}</p>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="border-t border-gray-200 p-6 bg-white rounded-b-xl">
            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                form="demo-request-form"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-primary border border-transparent rounded-lg hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.loading')}...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('common.submit')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default RequestDemoDrawer;
